"use client";

import { ChakraProvider, defaultSystem } from "@chakra-ui/react";
import { ColorModeProvider, type ColorModeProviderProps } from "./color-mode";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import {
	msalConfig,
	MSAL_REDIRECT_RESULT_KEY,
	MSAL_REDIRECT_ORIGIN_KEY,
} from "@/configs/msal-config";
import { useEffect, useState } from "react";

const msalInstance = new PublicClientApplication(msalConfig);

export function Provider(props: ColorModeProviderProps) {
	const [isMsalInitialized, setIsMsalInitialized] = useState(false);

	useEffect(() => {
		msalInstance
			.initialize()
			.then(() => msalInstance.handleRedirectPromise())
			.then((response) => {
				if (response?.idToken) {
					sessionStorage.setItem(
						MSAL_REDIRECT_RESULT_KEY,
						JSON.stringify({ idToken: response.idToken }),
					);
					const returnUrl = sessionStorage.getItem(MSAL_REDIRECT_ORIGIN_KEY);
					if (returnUrl) {
						sessionStorage.removeItem(MSAL_REDIRECT_ORIGIN_KEY);
						window.location.replace(returnUrl);
						return;
					}
				}
				setIsMsalInitialized(true);
			})
			.catch(console.error)
			// Always unblock rendering — handleRedirectPromise rejects on
			// OAuth fragments it did not issue (e.g. the native-app SSO
			// flow landing on /sso-callback), and the app must still mount.
			.finally(() => setIsMsalInitialized(true));
	}, []);

	if (!isMsalInitialized) {
		return null;
	}

	return (
		<ChakraProvider value={defaultSystem}>
			<MsalProvider instance={msalInstance}>
				<GoogleOAuthProvider
					clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ""}
				>
					{/* Force client-side only rendering for ColorModeProvider to avoid hydration issues */}
					{typeof window !== "undefined" ? (
						<ColorModeProvider {...props} />
					) : (
						<ColorModeProvider
							{...props}
							enableSystem={false}
							defaultTheme="light"
						/>
					)}
				</GoogleOAuthProvider>
			</MsalProvider>
		</ChakraProvider>
	);
}
